import { useState, useRef, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Mail, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthContext } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SupportChat() {
  const { user, token } = useContext(AuthContext);
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const getWelcomeMessage = (userName) => ({
    id: 1,
    role: 'assistant',
    content: `¡Hola${userName ? ', ' + userName.split(' ')[0] : ''}! 👋 Soy el asistente de AdPulse AI. ¿En qué puedo ayudarte hoy?`
  });

  const getStorageKey = () => user?.id ? `support_chat_${user.id}` : null;

  const loadHistory = () => {
    const key = getStorageKey();
    if (!key) return [getWelcomeMessage(user?.name || user?.email)];
    try {
      const saved = localStorage.getItem(key);
      if (saved) return JSON.parse(saved);
    } catch(e) {}
    return [getWelcomeMessage(user?.name || user?.email)];
  };

  const [messages, setMessages] = useState(() => loadHistory());
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    const key = getStorageKey();
    if (key && messages.length > 1) {
      try { localStorage.setItem(key, JSON.stringify(messages.slice(-30))); } catch(e) {}
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    // Check if user wants human support
    const lowerInput = input.toLowerCase();
    if (lowerInput.includes('soporte') || lowerInput.includes('humano') || lowerInput.includes('persona') || lowerInput.includes('support') || lowerInput.includes('human')) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: '¡Entendido! Te conecto con nuestro equipo de soporte. Por favor, usa el formulario de email para enviar tu consulta y te responderemos lo antes posible. 📧'
      }]);
      setLoading(false);
      setShowEmailForm(true);
      return;
    }

    try {
      const response = await axios.post(`${API}/support/chat`, {
        message: input.trim(),
        context: messages.slice(-6).map(m => ({ role: m.role, content: m.content }))
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta de nuevo o contacta a soporte@adpulseai.com'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) return;
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/support/email`, {
        subject: emailSubject,
        message: emailMessage,
        user_email: user?.email || 'anonymous@user.com',
        user_name: user?.name || 'Usuario Anónimo'
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: '✅ ¡Mensaje enviado correctamente! Nuestro equipo te responderá pronto al email registrado. ¿Hay algo más en lo que pueda ayudarte?'
      }]);
      setShowEmailForm(false);
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: '❌ Hubo un error al enviar el email. Por favor, intenta de nuevo o escríbenos directamente a soporte@adpulseai.com'
      }]);
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-r from-ai-glow to-brand-meta text-white shadow-lg shadow-ai-glow/30 flex items-center justify-center hover:scale-110 transition-transform ${isOpen ? 'hidden' : ''}`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="support-chat-button"
      >
        <MessageCircle className="w-6 h-6" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={`fixed bottom-20 right-6 z-40 w-[350px] sm:w-[400px] h-[500px] sm:h-[550px] rounded-2xl shadow-2xl flex flex-col overflow-hidden ${theme.isDark ? 'bg-[#0a0a0a] border border-white/10' : 'bg-white border border-gray-200'}`}
            data-testid="support-chat-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-ai-glow to-brand-meta p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Soporte AdPulse</h3>
                  <p className="text-white/70 text-xs">Asistente con IA</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowEmailForm(true)}
                  className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                  title="Enviar email a soporte"
                >
                  <Mail className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${theme.isDark ? 'bg-[#050505]' : 'bg-gray-50'}`}>
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] p-3 rounded-2xl text-sm whitespace-pre-wrap ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-ai-glow to-brand-meta text-white rounded-br-sm'
                      : theme.isDark 
                        ? 'bg-white/10 text-white rounded-bl-sm'
                        : 'bg-white text-gray-800 shadow-sm rounded-bl-sm'
                  }`}>
                    {message.content}
                  </div>
                </motion.div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className={`p-3 rounded-2xl rounded-bl-sm ${theme.isDark ? 'bg-white/10' : 'bg-white shadow-sm'}`}>
                    <Loader2 className={`w-5 h-5 animate-spin ${theme.isDark ? 'text-white' : 'text-gray-600'}`} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Email Form */}
            {showEmailForm && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`absolute inset-0 ${theme.isDark ? 'bg-[#0a0a0a]' : 'bg-white'} p-4 flex flex-col`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-semibold ${theme.text}`}>Contactar Soporte</h3>
                  <button onClick={() => setShowEmailForm(false)} className={`p-1 rounded ${theme.cardHover}`}>
                    <X className={`w-5 h-5 ${theme.textSecondary}`} />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Asunto"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className={`w-full p-3 rounded-lg mb-3 ${theme.isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-100 border-gray-200 text-gray-900'} border`}
                />
                <textarea
                  placeholder="Describe tu problema o consulta..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  className={`w-full flex-1 p-3 rounded-lg resize-none ${theme.isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-100 border-gray-200 text-gray-900'} border`}
                />
                <Button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
                  className="w-full mt-3 bg-gradient-to-r from-ai-glow to-brand-meta text-white rounded-full"
                >
                  {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Enviar a Soporte
                </Button>
              </motion.div>
            )}

            {/* Input */}
            <div className={`p-4 border-t ${theme.border}`}>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Escribe tu pregunta..."
                  className={`flex-1 p-3 rounded-full text-sm ${theme.isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/40' : 'bg-gray-100 border-gray-200 text-gray-900 placeholder:text-gray-400'} border focus:outline-none focus:ring-2 focus:ring-ai-glow/50`}
                  data-testid="chat-input"
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className="w-10 h-10 p-0 rounded-full bg-gradient-to-r from-ai-glow to-brand-meta text-white"
                  data-testid="chat-send-button"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
